import Lock from 'browser-tabs-lock';
import fetch from 'unfetch';

import {
  createQueryParams,
  runPopup,
  parseQueryResult,
  encode,
  createRandomString,
  runIframe,
  sha256,
  bufferToBase64UrlEncoded,
  validateCrypto,
  openPopup,
  getBaseUrl,
  getSessionApiBaseUrl,
  validateOptions,
  getLoggingFormat
} from './utils';

import { oauthToken } from './api';

import { getUniqueScopes } from './scope';

import {
  InMemoryCache,
  ICache,
  CacheKey,
  CacheManager,
  SessionStorageCache
} from './cache';

import TransactionManager from './transaction-manager';
import { verify as verifyIdToken } from './jwt';
import {
  AuthenticationError,
  GenericError,
  PopupTimeoutError,
  TimeoutError
} from './errors';

import {
  ClientStorage,
  CookieStorage,
  CookieStorageWithLegacySameSite,
  SessionStorage,
  LEGACY_PREFIX
} from './storage';

import {
  CACHE_LOCATION_MEMORY,
  DEFAULT_POPUP_CONFIG_OPTIONS,
  DEFAULT_AUTHORIZE_TIMEOUT_IN_SECONDS,
  MISSING_REFRESH_TOKEN_ERROR_MESSAGE,
  DEFAULT_SCOPE,
  RECOVERABLE_ERRORS,
  DEFAULT_SESSION_CHECK_EXPIRY_DAYS,
  INVALID_REFRESH_TOKEN_ERROR_MESSAGE,
  DEFAULT_NOW_PROVIDER,
  DEFAULT_ENV,
  DEFAULT_POPUP_TIMEOUT_IN_MS
} from './constants';

import {
  AuthClientOptions,
  BaseLoginOptions,
  AuthorizeOptions,
  RedirectLoginOptions,
  PopupLoginOptions,
  PopupConfigOptions,
  GetUserOptions,
  GetIdTokenClaimsOptions,
  RedirectLoginResult,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  LogoutOptions,
  RefreshTokenOptions,
  OAuthTokenOptions,
  CacheLocation,
  LogoutUrlOptions,
  User,
  IdToken,
  GetTokenSilentlyVerboseResponse,
  TokenEndpointResponse,
  AuthResultOption,
  PopUpWindowOptions,
  LoggingOptions
} from './global';

// @ts-ignore
import TokenWorker from './worker/token.worker.ts';
import { isIE11 } from './user-agent';
import { singlePromise, retryPromise } from './promise-utils';
import { CacheKeyManifest } from './cache/key-manifest';

/**
 * @ignore
 */
type GetTokenSilentlyResult = TokenEndpointResponse & {
  decodedToken: ReturnType<typeof verifyIdToken>;
  scope: string;
  oauthTokenScope?: string;
  audience: string;
};

/**
 * @ignore
 */
const lock = new Lock();

/**
 * @ignore
 */
const GET_TOKEN_SILENTLY_LOCK_KEY = 'auth.lock.getTokenSilently';

/**
 * @ignore
 */
const buildOrganizationHintCookieName = (clientId: string) =>
  `auth.${clientId}.organization_hint`;

/**
 * @ignore
 */
const OLD_IS_AUTHENTICATED_COOKIE_NAME = 'auth.is.authenticated';

/**
 * @ignore
 */
const buildIsAuthenticatedCookieName = (clientId: string) =>
  `auth.${clientId}.is.authenticated`;

const buildLegacyAuthCookieName = (clientId: string) =>
  `${LEGACY_PREFIX}_auth.${clientId}`;

/**
 * @ignore
 */
const cacheLocationBuilders: Record<string, () => ICache> = {
  memory: () => new InMemoryCache().enclosedCache,
  sessionstorage: () => new SessionStorageCache()
};

/**
 * @ignore
 */
const cacheFactory = (location: string) => {
  return cacheLocationBuilders[location];
};

/**
 * @ignore
 */
const supportWebWorker = () => !isIE11();

/**
 * @ignore
 */
const getTokenIssuer = (issuer: string, domainUrl: string) => {
  if (issuer) {
    return issuer.startsWith('https://') ? issuer : `https://${issuer}/`;
  }

  return `${domainUrl}/`;
};

/**
 * @ignore
 */
const getDomain = (domainUrl: string) => {
  if (!/^https?:\/\//.test(domainUrl)) {
    return `https://${domainUrl}`;
  }

  return domainUrl;
};

/**
 * @ignore
 */
const getCustomInitialOptions = (
  options: AuthClientOptions
): BaseLoginOptions => {
  const {
    advancedOptions,
    audience,
    authClient,
    authorizeTimeoutInSeconds,
    cacheLocation,
    client_id,
    issuer,
    leeway,
    max_age,
    redirect_uri,
    scope,
    useRefreshTokens,
    useCookiesForTransactions,
    useFormData,
    ...customParams
  } = options;
  return customParams;
};

/**
 * Auth SDK for Single Page Applications using [Authorization Code Grant Flow with PKCE]
 */
export default class AuthClient {
  private transactionManager: TransactionManager;
  private cacheManager: CacheManager;
  private customOptions: BaseLoginOptions;
  private popUpOptions: PopUpWindowOptions;
  private domainUrl: string;
  private tokenIssuer: string;
  private defaultScope: string;
  private scope: string;
  private cookieStorage: ClientStorage;
  private sessionCheckExpiryDays: number;
  private orgHintCookieName: string;
  private isAuthenticatedCookieName: string;
  private nowProvider: () => number | Promise<number>;

  cacheLocation: CacheLocation;
  private worker: Worker;
  private env: string;
  private client_id: string;
  private postLogoutRedirectUri: string;
  private loggingOptions: LoggingOptions;

  constructor (private options: AuthClientOptions) {
    validateOptions(options);

    typeof window !== 'undefined' && validateCrypto();

    if (options.cache && options.cacheLocation) {
      console.warn(
        'Both `cache` and `cacheLocation` options have been specified in the AuthClient configuration; ignoring `cacheLocation` and using `cache`.'
      );
    }

    let cache: ICache;

    if (options.cache) {
      cache = options.cache;
    } else {
      this.cacheLocation = options.cacheLocation || CACHE_LOCATION_MEMORY;

      if (!cacheFactory(this.cacheLocation)) {
        throw new Error(`Invalid cache location "${this.cacheLocation}"`);
      }

      cache = cacheFactory(this.cacheLocation)();
    }

    this.cookieStorage =
      options.legacySameSiteCookie === false
        ? CookieStorage
        : CookieStorageWithLegacySameSite;

    this.orgHintCookieName = buildOrganizationHintCookieName(
      this.options.client_id
    );

    this.isAuthenticatedCookieName = buildIsAuthenticatedCookieName(
      this.options.client_id
    );

    this.sessionCheckExpiryDays =
      options.sessionCheckExpiryDays || DEFAULT_SESSION_CHECK_EXPIRY_DAYS;

    const transactionStorage = options.useCookiesForTransactions
      ? this.cookieStorage
      : SessionStorage;

    this.popUpOptions = {
      intervalId: null,
      timeoutId: null
    };

    this.env = options.env !== undefined ? options.env : DEFAULT_ENV;
    this.scope =
      options.scope !== undefined ? options.scope + ' openid' : DEFAULT_SCOPE;

    this.transactionManager = new TransactionManager(
      transactionStorage,
      this.options.client_id
    );

    this.nowProvider = this.options.nowProvider || DEFAULT_NOW_PROVIDER;

    this.cacheManager = new CacheManager(
      cache,
      !cache.allKeys
        ? new CacheKeyManifest(cache, this.options.client_id)
        : null,
      this.nowProvider
    );

    this.domainUrl = getBaseUrl(this.env);
    this.tokenIssuer = getTokenIssuer(this.options.issuer, this.domainUrl);

    /**this.defaultScope = getUniqueScopes(
      'openid',
      this.options?.advancedOptions?.defaultScope !== undefined
        ? this.options.advancedOptions.defaultScope
        : DEFAULT_SCOPE
    );**/

    // If using refresh tokens, automatically specify the `offline_access` scope.
    // Note we cannot add this to 'defaultScope' above as the scopes are used in the
    // cache keys - changing the order could invalidate the keys
    /**if (this.options.useRefreshTokens) {
      this.scope = getUniqueScopes(this.scope, 'offline_access');
    }**/

    // Don't use web workers unless using refresh tokens in memory and not IE11
    if (
      typeof window !== 'undefined' &&
      window.Worker &&
      this.options.useRefreshTokens &&
      this.cacheLocation === CACHE_LOCATION_MEMORY &&
      supportWebWorker()
    ) {
      this.worker = new TokenWorker();
    }

    this.customOptions = getCustomInitialOptions(options);
    this.loggingOptions = {
      method: '',
      isMethodDeprecated: false,
      cacheLocation: this.cacheLocation,
      useRefreshTokens: this.options.useRefreshTokens
    };
  }

  private _url (env: string, path: string) {
    return `${getBaseUrl(env)}${path}`;
  }

  private _getParams (
    authorizeOptions: BaseLoginOptions,
    state: string,
    nonce: string,
    code_challenge: string,
    redirect_uri: string
  ): AuthorizeOptions {
    const {
      leeway,
      useRefreshTokens,
      useCookiesForTransactions,
      useFormData,
      authClient,
      cacheLocation,
      advancedOptions,
      detailedResponse,
      ...withoutClientOptions
    } = this.options;

    return {
      ...withoutClientOptions,
      ...authorizeOptions,
      scope: this.scope,
      response_type: 'code',
      state,
      nonce,
      redirect_uri: redirect_uri || this.options.redirect_uri,
      code_challenge,
      code_challenge_method: 'S256'
    };
  }
  private _authorizeUrl (authorizeOptions: AuthorizeOptions) {
    return this._url(
      this.env,
      `/authorize?${createQueryParams(authorizeOptions)}`
    );
  }

  private async _verifyIdToken (access_token: string, expires_in: number) {
    const now = await this.nowProvider();

    return verifyIdToken({
      access_token,
      expires_in
    });
  }

  private _parseNumber (value: any): number {
    if (typeof value !== 'string') {
      return value;
    }
    return parseInt(value, 10) || undefined;
  }

  private _processOrgIdHint (organizationId?: string) {
    if (organizationId) {
      this.cookieStorage.save(this.orgHintCookieName, organizationId);
    } else {
      this.cookieStorage.remove(this.orgHintCookieName);
    }
  }

  /**
   * ```js
   * await auth.buildAuthorizeUrl(options);
   * ```
   *
   * Builds an `/authorize` URL for loginWithRedirect using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   * @deprecated This function is being deprecated, please update your code by July 31 2023
   */
  public async buildAuthorizeUrl (
    options: RedirectLoginOptions = {}
  ): Promise<string> {
    const { redirect_uri, appState, state, ...authorizeOptions } = options;

    const stateIn = state ? state : encode(createRandomString());
    const nonceIn = encode(createRandomString());
    const code_verifier = createRandomString();
    const code_challengeBuffer = await sha256(code_verifier);
    localStorage.setItem('pkce_code_verifier', code_verifier);
    let appStateStr = '';
    if (appState) {
      if (typeof appState === 'object') {
        const stringifiedState = JSON.stringify(appState);
        appStateStr = stringifiedState ? stringifiedState : '';
      } else {
        appStateStr = appState.toString();
      }
    }
    localStorage.setItem('app_state', appStateStr);
    const code_challenge = bufferToBase64UrlEncoded(code_challengeBuffer);
    const fragment = options.fragment ? `#${options.fragment}` : '';

    const params = this._getParams(
      authorizeOptions,
      stateIn,
      nonceIn,
      code_challenge,
      redirect_uri
    );

    const url = this._authorizeUrl(params);
    const organizationId = options.organization || this.options.organization;

    this.transactionManager.create({
      nonce: nonceIn,
      code_verifier,
      appState,
      scope: params.scope,
      audience: params.audience || 'default',
      redirect_uri: params.redirect_uri,
      ...(organizationId && { organizationId })
    });

    return url + fragment;
  }

  /**
   * ```js
   * try {
   *  await auth.loginWithPopup();
   * } catch(e) {
   *  if (e instanceof PopupCancelledError) {
   *    // Popup was closed before login completed
   *  }
   * }
   * ```
   *
   * Opens a popup with the `/authorize` URL using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated. If the response is successful,
   * results will be valid according to their expiration times.
   *
   * IMPORTANT: This method has to be called from an event handler
   * that was started by the user like a button click, for example,
   * otherwise the popup will be blocked in most browsers.
   *

  // 
  //  */
  public async loginWithPopup (): Promise<string> {
    const stateIn = encode(createRandomString());
    this.cleanupPopup(true);
    const redirect_uri =
      this.options.redirect_uri_popup || this.options.redirect_uri;
    const url = await this.buildAuthorizeUrl({
      redirect_uri: redirect_uri,
      state: stateIn
    });
    this.popUpOptions.popupWindow = openPopup(url);

    if (!this.popUpOptions.popupWindow) {
      throw GenericError.fromPayload({
        error: 'open_popup_failed',
        error_description:
          'Unable to open a popup for loginWithPopup - window.open returned `null`'
      });
    }

    const codeResult = await runPopup(
      this.popUpOptions,
      redirect_uri,
      this.cleanupPopup.bind(this)
    );

    if (stateIn !== codeResult.state) {
      throw GenericError.fromPayload({
        error: 'invalid_state',
        error_description: 'Invalid State'
      });
    }

    try {
      this.loggingOptions.method = 'loginWithPopup';
      this.loggingOptions.isMethodDeprecated = false;

      const authResult = await oauthToken(
        {
          grant_type: 'authorization_code',
          client_id: this.options.client_id,
          code_verifier: localStorage.getItem('pkce_code_verifier'),
          code: codeResult.code,
          scope: this.scope,
          audience: this.domainUrl,
          baseUrl: getBaseUrl(this.env),
          redirect_uri: redirect_uri,
          ...this.customOptions
        } as OAuthTokenOptions,
        this.worker,
        this.loggingOptions
      );

      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;

      const decodedToken = await this._verifyIdToken(
        authResult.access_token,
        authResult.expires_in
      );

      const cacheEntry = {
        ...authResult,
        decodedToken,
        audience: this.options.audience || 'default',
        scope: this.scope,
        ...(authResult.scope ? { oauthTokenScope: authResult.scope } : null),
        client_id: this.options.client_id
      };

      await this.cacheManager.set(cacheEntry);

      this.cookieStorage.save(this.isAuthenticatedCookieName, true, {
        daysUntilExpire: this.sessionCheckExpiryDays
      });

      if (authResult.access_token) {
        localStorage.setItem('auth.session', 'true');
      }
      this._processOrgIdHint(decodedToken.claims.org_id);
      return authResult.access_token;
    } catch (e) {
      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;

      throw GenericError.fromPayload({
        error: 'failed_to_fetch_token',
        error_description:
          e.message || e.error_description || 'Failed to fetch token'
      });
    }
  }

  /**
   * ```js
   * await auth.closePopup();
   * ```
   *
   * Closes the popup instance that is already open
   * If no popup instance available, it will throw an error - 'close_popup_failed'
   *
   */
  public async closePopup () {
    this.cleanupPopup();
  }

  private cleanupPopup (isCalledInternally = false) {
    if (this.popUpOptions.popupWindow) {
      this.popUpOptions.popupWindow.close();
    } else if (!isCalledInternally) {
      throw GenericError.fromPayload({
        error: 'close_popup_failed',
        error_description: 'There is no popup instance available to be closed'
      });
    }

    if (this.popUpOptions.intervalId) {
      clearInterval(this.popUpOptions.intervalId);
    }

    if (this.popUpOptions.timeoutId) {
      clearTimeout(this.popUpOptions.timeoutId);
    }

    this.popUpOptions.intervalId = null;
    this.popUpOptions.timeoutId = null;
    this.popUpOptions.popupWindow = null;
  }

  /**
   * ```js
   * const user = await auth.getUser();
   * ```
   *
   * Returns the user information if available (decoded
   * from the `id_token`).
   *
   * If you provide an audience or scope, they should match an existing Access Token
   * (the SDK stores a corresponding ID Token with every Access Token, and uses the
   * scope and audience to look up the ID Token)
   *
   * @typeparam TUser The type to return, has to extend {@link User}.
   * @param options
   * @deprecated This function is being deprecated, please update your code by July 31 2023
   */
  public async getUser<TUser extends User> (
    options: GetUserOptions = {}
  ): Promise<TUser | undefined> {
    const audience = options.audience || this.options.audience || 'default';
    const scope = getUniqueScopes(this.defaultScope, this.scope, options.scope);

    const cache = await this.cacheManager.get(
      new CacheKey({
        client_id: this.options.client_id,
        audience,
        scope
      })
    );

    return cache && cache.decodedToken && (cache.decodedToken.user as TUser);
  }

  /**
   * ```js
   * const claims = await auth.getIdTokenClaims();
   * ```
   *
   * Returns all claims from the id_token if available.
   *
   * If you provide an audience or scope, they should match an existing Access Token
   * (the SDK stores a corresponding ID Token with every Access Token, and uses the
   * scope and audience to look up the ID Token)
   *
   * @param options
   * @deprecated This function is being deprecated, please update your code by July 31 2023
   */
  public async getIdTokenClaims (
    options: GetIdTokenClaimsOptions = {}
  ): Promise<IdToken> {
    const audience = options.audience || this.options.audience || 'default';
    const scope = getUniqueScopes(this.defaultScope, this.scope, options.scope);

    const cache = await this.cacheManager.get(
      new CacheKey({
        client_id: this.options.client_id,
        audience,
        scope
      })
    );

    return cache && cache.decodedToken && cache.decodedToken.claims;
  }

  /**
   * ```js
   * await auth.loginWithRedirect(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async loginWithRedirect (options: RedirectLoginOptions = {}) {
    const { redirectMethod, redirectTarget, ...urlOptions } = options;
    const url = await this.buildAuthorizeUrl(urlOptions);
    const target = redirectTarget ?? window.location;
    target[redirectMethod || 'assign'](url);
  }

  /**
   * After the browser redirects back to the callback page,
   * call `handleRedirectCallback` to handle success and error
   * responses from Auth. If the response is successful, results
   * will be valid according to their expiration times.
   */
  public async handleRedirectCallback (
    url: string = window.location.href
  ): Promise<RedirectLoginResult> {
    const queryStringFragments = url.split('?').slice(1);

    if (queryStringFragments.length === 0) {
      throw new Error('There are no query params available for parsing.');
    }

    const { state, code, error, error_description } = parseQueryResult(
      queryStringFragments.join('')
    );

    // Local Storage should have a `code_verifier` to do PKCE Verification for CSRF protection
    if (!localStorage.getItem('pkce_code_verifier')) {
      throw new Error('Invalid state');
    }
    const appStateStr = localStorage.getItem('app_state');
    let appState;
    
    if(appStateStr) {
      try {
        appState = JSON.parse(appStateStr);
      } catch (e) {
        appState = appStateStr;
      } 
    }

    if (error) {
      throw new AuthenticationError(error, error_description, state, appState);
    }

    const tokenOptions = {
      audience: this.options.audience || 'default',
      scope: this.scope,
      baseUrl: this.domainUrl,
      client_id: this.options.client_id,
      code_verifier: localStorage.getItem('pkce_code_verifier'),
      grant_type: 'authorization_code',
      code
    } as OAuthTokenOptions;
    // some old versions of the SDK might not have added redirect_uri to the
    // transaction, we dont want the key to be set to undefined.
    if (undefined !== this.options.redirect_uri) {
      tokenOptions.redirect_uri = this.options.redirect_uri;
    }

    this.loggingOptions.method = 'handleRedirectCallback';
    this.loggingOptions.isMethodDeprecated = false;

    const authResult = await oauthToken(
      tokenOptions,
      this.worker,
      this.loggingOptions
    );

    this.loggingOptions.method = '';
    this.loggingOptions.isMethodDeprecated = false;

    const decodedToken = await this._verifyIdToken(
      authResult.access_token,
      authResult.expires_in
    );

    await this.cacheManager.set({
      ...authResult,
      decodedToken,
      audience: this.options.audience || 'default',
      scope: this.scope,
      ...(authResult.scope ? { oauthTokenScope: authResult.scope } : null),
      client_id: this.options.client_id
    });

    this.cookieStorage.save(this.isAuthenticatedCookieName, true, {
      daysUntilExpire: this.sessionCheckExpiryDays
    });

    if (authResult.access_token) {
      localStorage.setItem('auth.session', 'true');
    }

    this._processOrgIdHint(decodedToken.claims.org_id);

    return {
      appState: appState
    };
  }

  /**
   * ```js
   * await auth.checkSession();
   * ```
   *
   * Check if the user is logged in using `getTokenSilently`. The difference
   * with `getTokenSilently` is that this doesn't return a token, but it will
   * pre-fill the token cache.
   *
   * This method also heeds the `auth.{clientId}.is.authenticated` cookie, as an optimization
   *  to prevent calling Auth unnecessarily. If the cookie is not present because
   * there was no previous login (or it has expired) then tokens will not be refreshed.
   *
   * It should be used for silently logging in the user when you instantiate the
   * `AuthClient` constructor. You should not need this if you are using the
   * `createAuthClient` factory.
   *
   * @param options
   * @deprecated This function is being deprecated, please update your code by July 31 2023
   */
  public async checkSession (options?: GetTokenSilentlyOptions) {
    if (!this.cookieStorage.get(this.isAuthenticatedCookieName)) {
      if (!this.cookieStorage.get(OLD_IS_AUTHENTICATED_COOKIE_NAME)) {
        return;
      } else {
        // Migrate the existing cookie to the new name scoped by client ID
        this.cookieStorage.save(this.isAuthenticatedCookieName, true, {
          daysUntilExpire: this.sessionCheckExpiryDays
        });

        this.cookieStorage.remove(OLD_IS_AUTHENTICATED_COOKIE_NAME);
      }
    }

    try {
      this.loggingOptions.method = 'checkSession';
      this.loggingOptions.isMethodDeprecated = true;
      await this.getAccessTokenSilently(options);
    } catch (error) {
      if (!RECOVERABLE_ERRORS.includes(error.error)) {
        throw error;
      }
    }
  }

  /**
   * @deprecated Deprecate getTokenSilently() in place of getAccessTokenSilently() and getIDTokenSilently()
   */
  public async getTokenSilently (
    options: GetTokenSilentlyOptions = {}
  ): Promise<string | GetTokenSilentlyVerboseResponse> {
    console.warn('getTokenSilently() is deprecated!');

    const { ignoreCache, ...getTokenOptions } = {
      audience: this.options.audience,
      ignoreCache: false,
      ...options,
      scope: getUniqueScopes(this.scope)
    };

    this.loggingOptions.method = 'getTokenSilently';
    this.loggingOptions.isMethodDeprecated = true;

    return singlePromise(
      () =>
        this._getTokenSilently(
          options?.detailedResponse ? 'detailedResponse' : 'accessToken',
          { ignoreCache, ...getTokenOptions }
        ),
      `${this.options.client_id}::${getTokenOptions.audience}::${getTokenOptions.scope}`
    );
  }

  public async getAccessTokenSilently (
    options: GetTokenSilentlyOptions = {}
  ): Promise<string> {
    const { ignoreCache, ...getTokenOptions } = {
      audience: this.options.audience,
      ignoreCache: false,
      ...options,
      scope: getUniqueScopes(this.scope)
    };
    if (this.loggingOptions.method !== 'checkSession') {
      this.loggingOptions.method = 'getAccessTokenSilently';
      this.loggingOptions.isMethodDeprecated = false;
    }
    return singlePromise(
      () =>
        this._getTokenSilently('accessToken', {
          ignoreCache,
          ...getTokenOptions
        }),
      `${this.options.client_id}::${getTokenOptions.audience}::${getTokenOptions.scope}`
    );
  }

  public async getIDTokenSilently (
    options: GetTokenSilentlyOptions = {}
  ): Promise<string> {
    const { ignoreCache, ...getTokenOptions } = {
      audience: this.options.audience,
      ignoreCache: false,
      ...options,
      scope: getUniqueScopes(this.scope)
    };

    this.loggingOptions.method = 'getIDTokenSilently';
    this.loggingOptions.isMethodDeprecated = false;

    return singlePromise(
      () =>
        this._getTokenSilently('idToken', { ignoreCache, ...getTokenOptions }),
      `${this.options.client_id}::${getTokenOptions.audience}::${getTokenOptions.scope}`
    );
  }

  private async _getTokenSilently (
    authResultOption: AuthResultOption = 'accessToken',
    options: GetTokenSilentlyOptions = {}
  ): Promise<string | GetTokenSilentlyVerboseResponse> {
    const { ignoreCache, ...getTokenOptions } = options;

    const getAccessTokenFromCache = async () => {
      const cache = await this.cacheManager.get(
        new CacheKey({
          scope: getTokenOptions.scope,
          audience: getTokenOptions.audience || 'default',
          client_id: this.options.client_id
        }),
        60 // get a new token if within 60 seconds of expiring
      );

      return cache;
    };

    if (!ignoreCache) {
      const cache = await getAccessTokenFromCache();

      if (cache) {
        if ('accessToken' === authResultOption && cache?.access_token) {
          return cache.access_token;
        }

        if ('idToken' === authResultOption && cache?.id_token) {
          return cache.id_token;
        }

        if (cache.refresh_token) {
          const authResult = await this._getTokenUsingRefreshToken(
            getTokenOptions
          );
          await this.cacheManager.set({
            client_id: this.options.client_id,
            ...authResult
          });

          this.cookieStorage.save('auth.is.authenticated', true, {
            daysUntilExpire: this.sessionCheckExpiryDays
          });

          if ('accessToken' === authResultOption) {
            return authResult.access_token;
          }

          if ('idToken' === authResultOption) {
            return authResult.id_token;
          }
        }
      }
    }

    if (
      await retryPromise(
        () => lock.acquireLock(GET_TOKEN_SILENTLY_LOCK_KEY, 5000),
        10
      )
    ) {
      try {
        // Check the cache a second time, because it may have been populated
        // by a previous call while this call was waiting to acquire the lock.
        if (!ignoreCache) {
          const cache = await getAccessTokenFromCache();

          if ('accessToken' === authResultOption && cache?.access_token) {
            return cache.access_token;
          }

          if ('idToken' === authResultOption && cache?.id_token) {
            return cache.id_token;
          }
        }

        const authResult = this.options.useRefreshTokens
          ? await this._getTokenUsingRefreshToken(getTokenOptions)
          : await this._getTokenFromIFrame(getTokenOptions);

        await this.cacheManager.set({
          client_id: this.options.client_id,
          ...authResult
        });

        this.cookieStorage.save(this.isAuthenticatedCookieName, true, {
          daysUntilExpire: this.sessionCheckExpiryDays
        });

        if (
          options.detailedResponse ||
          'detailedResponse' === authResultOption
        ) {
          const { id_token, access_token, oauthTokenScope, expires_in } =
            authResult;

          return {
            id_token,
            access_token,
            ...(oauthTokenScope ? { scope: oauthTokenScope } : null),
            expires_in
          };
        }

        if (authResult.access_token) {
          localStorage.setItem('auth.session', 'true');
        }

        if ('idToken' === authResultOption) {
          return authResult.id_token;
        }

        return authResult.access_token;
      } finally {
        await lock.releaseLock(GET_TOKEN_SILENTLY_LOCK_KEY);
      }
    } else {
      throw new TimeoutError();
    }
  }

  /**
   * ```js
   * const token = await auth.getTokenWithPopup(options);
   * ```
   * Opens a popup with the `/authorize` URL using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated. If the response is successful,
   * results will be valid according to their expiration times.
   *
   * @param options
   * @param config
   * @deprecated This function is being deprecated, please update your code by July 31 2023
   */
  public async getTokenWithPopup (
    options: GetTokenWithPopupOptions = {},
    config: PopupConfigOptions = {}
  ) {
    options.audience = options.audience || this.options.audience;

    options.scope = getUniqueScopes(
      this.defaultScope,
      this.scope,
      options.scope
    );

    config = {
      ...DEFAULT_POPUP_CONFIG_OPTIONS,
      ...config
    };

    await this.loginWithPopup();

    const cache = await this.cacheManager.get(
      new CacheKey({
        scope: options.scope,
        audience: options.audience || 'default',
        client_id: this.options.client_id
      })
    );

    return cache.access_token;
  }

  /**
   * ```js
   * const isAuthenticated = await auth.isAuthenticated();
   * ```
   *
   * Returns `true` if there's valid information stored,
   * otherwise returns `false`.
   *
   */
  public async isAuthenticated () {
    const user = await this.getUser();
    return !!user;
  }

  /**
   * ```js
   * await auth.buildLogoutUrl(options);
   * ```
   *
   * Builds a URL to the logout endpoint using the parameters provided as arguments.
   * @param options
   */
  public buildLogoutUrl (options: LogoutUrlOptions = {}): string {
    if (options.client_id !== null) {
      options.client_id = options.client_id || this.options.client_id;
    } else {
      delete options.client_id;
    }

    const { federated, ...logoutOptions } = options;
    //const federatedQuery = federated ? `&federated` : '';
    const url = this._url(
      this.env,
      `/logout?${createQueryParams(logoutOptions)}`
    );

    return url;
  }

  /**
   * ```js
   * auth.logout();
   * ```
   *
   * Clears the application session and performs a redirect to `/v2/logout`, using
   * the parameters provided as arguments, to clear the Auth session.
   *
   * **Note:** If you are using a custom cache, and specifying `localOnly: true`, and you want to perform actions or read state from the SDK immediately after logout, you should `await` the result of calling `logout`.
   *
   * If the `federated` option is specified it also clears the Identity Provider session.
   * If the `localOnly` option is specified, it only clears the application session.
   * It is invalid to set both the `federated` and `localOnly` options to `true`,
   * and an error will be thrown if you do.
   *
   *
   * @param options
   */
  public async logout (options: LogoutOptions = {}) {
    const { localOnly, ...logoutOptions } = options;

    if (localOnly && logoutOptions.federated) {
      throw new Error(
        'It is invalid to set both the `federated` and `localOnly` options to `true`'
      );
    }

    const getAccessTokenFromCache = async () => {
      const cache = await this.cacheManager.get(
        new CacheKey({
          scope: this.scope,
          audience: this.options.audience || 'default',
          client_id: this.options.client_id
        })
      );
      return cache;
    };

    const cache = await getAccessTokenFromCache();
    //invalidate tokens
    if (cache) {
      if (cache.access_token) {
        await this._invalidateToken(cache.access_token, 'access_token');
      }

      if (cache.refresh_token) {
        await this._invalidateToken(cache.refresh_token, 'refresh_token');
      }
    }
    const legacyauthCookie = buildLegacyAuthCookieName(this.options.client_id);

    const postCacheClear = () => {
      localStorage.removeItem('pkce_code_verifier');
      localStorage.removeItem('app_state');
      localStorage.removeItem('auth.session');
      this.cookieStorage.remove(this.orgHintCookieName);
      this.cookieStorage.remove(this.isAuthenticatedCookieName);
      this.cookieStorage.remove(legacyauthCookie);

      if (localOnly) {
        return;
      }

      const url = this.buildLogoutUrl(logoutOptions);

      window.location.assign(url);
    };

    if (this.options.cache) {
      return this.cacheManager.clear().then(() => postCacheClear());
    } else {
      this.cacheManager.clearSync();
      postCacheClear();
    }
  }

  private async _getTokenFromIFrame (
    options: GetTokenSilentlyOptions
  ): Promise<GetTokenSilentlyResult> {
    const stateIn = encode(createRandomString());
    const nonceIn = encode(createRandomString());
    const code_verifier = localStorage.getItem('pkce_code_verifier');
    const code_challengeBuffer = await sha256(code_verifier);
    const code_challenge = bufferToBase64UrlEncoded(code_challengeBuffer);

    const { detailedResponse, ...withoutClientOptions } = options;

    const params = this._getParams(
      withoutClientOptions,
      stateIn,
      nonceIn,
      code_challenge,
      options.redirect_uri ||
        this.options.redirect_uri ||
        window.location.origin
    );

    const orgIdHint = this.cookieStorage.get<string>(this.orgHintCookieName);

    if (orgIdHint && !params.organization) {
      params.organization = orgIdHint;
    }

    if (this.options.redirect_uri_iframe) {
      params.redirect_uri = this.options.redirect_uri_iframe;
    }
    const url =
      (await this.buildAuthorizeUrl({
        redirect_uri: params.redirect_uri
      })) + '&prompt=none';

    const timeout =
      options.timeoutInSeconds || this.options.authorizeTimeoutInSeconds;

    try {
      // When a browser is running in a Cross-Origin Isolated context, using iframes is not possible.
      // It doesn't throw an error but times out instead, so we should exit early and inform the user about the reason.
      // https://developer.mozilla.org/en-US/docs/Web/API/crossOriginIsolated
      if ((window as any).crossOriginIsolated) {
        throw new GenericError(
          'login_required',
          'The application is running in a Cross-Origin Isolated context, silently retrieving a token without refresh token is not possible.'
        );
      }
      const codeResult = await runIframe(url, this.domainUrl, timeout);

      const {
        code,
        scope,
        audience,
        redirect_uri,
        ignoreCache,
        timeoutInSeconds,
        detailedResponse,
        ...customOptions
      } = options;

      const tokenResult = await oauthToken(
        {
          grant_type: 'authorization_code',
          client_id: this.options.client_id,
          code_verifier: localStorage.getItem('pkce_code_verifier'),
          code: codeResult.code,
          scope: this.scope,
          audience: this.domainUrl,
          baseUrl: getBaseUrl(this.env),
          redirect_uri: params.redirect_uri,
          ...this.customOptions,
          ...customOptions
        } as OAuthTokenOptions,
        this.worker,
        this.loggingOptions
      );

      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;

      const decodedToken = await this._verifyIdToken(
        tokenResult.access_token,
        tokenResult.expires_in
      );

      this._processOrgIdHint(decodedToken.claims.org_id);

      return {
        ...tokenResult,
        decodedToken,
        scope: params.scope,
        oauthTokenScope: tokenResult.scope,
        audience: params.audience || 'default'
      };
    } catch (e) {
      if (e.error === 'login_required') {
        this.logout({
          localOnly: true
        });
      }
      throw e;
    }
  }

  private async _getTokenUsingRefreshToken (
    options: GetTokenSilentlyOptions
  ): Promise<GetTokenSilentlyResult> {
    options.scope = getUniqueScopes(
      this.defaultScope,
      this.options.scope,
      options.scope
    );

    const cache = await this.cacheManager.get(
      new CacheKey({
        scope: options.scope,
        audience: options.audience || 'default',
        client_id: this.options.client_id
      })
    );

    // If you don't have a refresh token in memory
    // and you don't have a refresh token in web worker memory
    // fallback to an iframe.
    if (!cache || !cache.refresh_token) {
      return await this._getTokenFromIFrame(options);
    }

    const redirect_uri =
      options.redirect_uri ||
      this.options.redirect_uri ||
      window.location.origin;

    let tokenResult: TokenEndpointResponse;

    const {
      scope,
      audience,
      ignoreCache,
      timeoutInSeconds,
      detailedResponse,
      ...customOptions
    } = options;

    const timeout =
      typeof options.timeoutInSeconds === 'number'
        ? options.timeoutInSeconds * 1000
        : null;

    try {
      tokenResult = await oauthToken(
        {
          ...this.customOptions,
          ...customOptions,
          grant_type: 'refresh_token',
          refresh_token: cache.refresh_token,
          client_id: this.options.client_id,
          scope: this.scope,
          audience: this.domainUrl,
          baseUrl: getBaseUrl(this.env),
          redirect_uri: this.options.redirect_uri,
          ...(timeout && { timeout })
        } as RefreshTokenOptions,
        this.worker,
        this.loggingOptions
      );
    } catch (e) {
      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;
      if (
        // The web worker didn't have a refresh token in memory so
        // fallback to an iframe.
        e.message === MISSING_REFRESH_TOKEN_ERROR_MESSAGE ||
        // A refresh token was found, but is it no longer valid.
        // Fallback to an iframe.
        (e.message &&
          e.message.indexOf(INVALID_REFRESH_TOKEN_ERROR_MESSAGE) > -1)
      ) {
        return await this._getTokenFromIFrame(options);
      }

      throw e;
    }

    this.loggingOptions.method = '';
    this.loggingOptions.isMethodDeprecated = false;

    const decodedToken = await this._verifyIdToken(
      tokenResult.access_token,
      tokenResult.expires_in
    );

    return {
      ...tokenResult,
      decodedToken,
      scope: options.scope,
      oauthTokenScope: tokenResult.scope,
      audience: options.audience || 'default'
    };
  }

  private async _getEntryFromCache ({
    scope,
    audience,
    client_id,
    getDetailedEntry = true
  }: {
    scope: string;
    audience: string;
    client_id: string;
    getDetailedEntry?: boolean;
  }) {
    const entry = await this.cacheManager.get(
      new CacheKey({
        scope,
        audience,
        client_id
      }),
      60 // get a new token if within 60 seconds of expiring
    );

    if (entry && entry.access_token) {
      if (getDetailedEntry) {
        const { id_token, access_token, oauthTokenScope, expires_in } = entry;

        return {
          id_token,
          access_token,
          ...(oauthTokenScope ? { scope: oauthTokenScope } : null),
          expires_in
        };
      }

      return entry.access_token;
    }
  }

  private async _invalidateToken (
    token: string,
    tokenType: string
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const url = getBaseUrl(this.env) + '/revoke';
        const body = {
          token: token,
          token_type_hint: tokenType,
          client_id: this.options.client_id
        };

        const reqbody = createQueryParams(body);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json, text/plain,*/*'
          },
          body: reqbody
        });
        const output = response.statusText;
        return resolve(output);
      } catch (error) {
        return reject('Failed to revoke token');
      }
    });
  }

  /**
   * *
   * @param accessToken
   * @deprecated This function is being deprecated and will be removed in future. Please reach out to #userprofile-support channel on slack for more details.
   */
  public async getUserProfile (accessToken: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (accessToken) {
        try {
          const url =
            getBaseUrl(this.env).split('/authentication')[0] +
            '/userprofile/v1/users/@me';
          const response = await fetch(url, {
            headers: {
              Authorization: 'Bearer ' + accessToken,
              'Content-Type': 'application/json'
            }
          });
          const output = await response.json();
          return resolve(output);
        } catch (error) {
          return reject('Failed to fetch user profile');
        }
      }

      return reject(new Error('No access token was provided.'));
    });
  }

  /**
   * *
   * @param accessToken
   * @deprecated This function is being deprecated, please update your code by July 31 2023
   */
  public async getSession (accessToken: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (accessToken) {
        try {
          const url = getSessionApiBaseUrl(this.env) + '/sessions/@me';
          const response = await fetch(url, { credentials: 'include' });
          const output = await response.json();
          return resolve(output);
        } catch (error) {
          return reject('Failed to get the session');
        }
      }

      return reject(new Error('No access token was provided.'));
    });
  }
}
